<template>
    <div :class="['multiSelect', {closed: !isOpen, open: isOpen, hasSelection: selectedCount > 0, hasIcons: showIcons}]">
        <button type="button" class="dropdown-toggle" :id="getButtonId" aria-haspopup="true" :aria-expanded="isAriaExpanded" :aria-controls="getDropdownId" @click="toggleMenu">
            <span class="itemCount" v-if="selectedCount > 0">{{selectedCount}}</span>
            <span class="tagIcon" v-if="selectedCount == 0">
                <svg width="24" height="24" aria-hidden="true"><use xlink:href="#icon_tag"></use></svg>
            </span>
            <span class="filterLabel">{{ defaultLabel }}</span>
            <span class="dropdownIcon">
                <svg width="24" height="24" aria-hidden="true"><use xlink:href="#icon_dropdown"></use></svg>
            </span>
        </button>

        <ul class="dropdown-menu" :id="getDropdownId" role="menubar" :aria-labelledby="getButtonId">
            <li role="none">
                <search-input v-if="showSearch && isOpen"
                              :searchQuery="searchQuery"
                              :placeholder="searchPlaceholder"
                              :search-aria-label="searchAriaLabel"
                              @search_input="onSearchInput"
                              ref="searchInput" />
                <ul role="group" aria-label="Unselected" class="multiSelect__unselected-list" :class="{'multiSelect__unselected-list--with-search': showSearch}" ref="unselectedList">
                    <li v-for="item in unselectedItems" :key="item.id" role="none">
                        <button type="button" @click="itemSelected(item)" role="menuitem">
                            <span v-if="showIcons" class="customIcon" :data-svg="item.id"></span>
                            <span class="selectIcon">
                                <svg width="17" height="17" aria-hidden="true">
                                    <use xlink:href="#icon_expand"></use>
                                </svg>
                            </span>
                            {{ item.name }}
                        </button>
                    </li>
                </ul>
            </li>
            <li role="group" aria-label="Selected">
                <ul class="multiSelect__selected-list" v-if="selectedCount > 0">
                    <li v-for="item in selectedItems" :key="item.id">
                        <button type="button" @click="itemUnselected(item)">
                            <span v-if="showIcons" class="customIcon" :data-svg="item.id"></span>
                            <span class="selectIcon">
                                <svg width="17" height="17" aria-hidden="true">
                                    <use xlink:href="#icon_close"></use>
                                </svg>
                            </span>
                            {{ item.name }}
                        </button>
                    </li>
                </ul>
            </li>
        </ul>
    </div>
</template>
<script>
    import { forEach as _forEach } from 'lodash';
    import { concat as _concat } from 'lodash';
    import SearchInput from './SearchInput.vue';

    export default {
        name: 'MultiSelector',
        components: { SearchInput },
        props: {
            rawList: { type: Array, required: true },
            showIcons: { type: Boolean, default: false },
            showSearch: { type: Boolean, default: false },
            searchPlaceholder: { type: String, default: '' },
            searchAriaLabel: { type: String, default: false },
            bus: { type: Object, default: null },
            defaultLabel: { type: String, default: '' },
            indexId: { type: [String, Number], default: null }
        },
        inject: {
            parentBus: { default: null }
        },
        data() {
            const list = Array.isArray(this.rawList) ? this.rawList : [];
            return {
                items: list.slice(),
                selectedItems: list.filter(item => item && item.selected === true),
                isOpen: false,
                searchQuery: ''
            };
        },
        computed: {
            unselectedItems() {
                const unselected = this.items.filter(item => !item || item.selected !== true);
                if (!this.searchQuery) {
                    return unselected;
                }
                const query = this.searchQuery.toLowerCase();
                return unselected.filter(item => item && item.name && item.name.toLowerCase().includes(query));
            },
            selectedCount() {
                return this.selectedItems.length;
            },
            isAriaExpanded() {
                return this.isOpen ? 'true' : 'false';
            },
            getButtonId() {
                return `multi-selector-button-${this.indexId || ''}`;
            },
            getDropdownId() {
                return `multi-selector-dropdown-${this.indexId || ''}`;
            }
        },
        created() {
            // find nearest ancestor bus (walk up parent chain)
            let parent = this.$parent;
            let foundBus = null;
            while (parent) {
                if (parent.bus && typeof parent.bus.$on === 'function') {
                    foundBus = parent.bus;
                    break;
                }
                parent = parent.$parent;
            }
            const bus = this.bus || this.parentBus || foundBus;
            if (bus && typeof bus.$on === 'function') {
                bus.$on('hideOthers', (clickedItem) => {
                    if (clickedItem !== this) {
                        this.isOpen = false;
                        this.searchQuery = '';
                    }
                });
                bus.$on('clearAll', () => {
                    this.items = this.items.map(i => i ? { ...i, selected: false } : i);
                    if (this.showIcons) {
                        this.$nextTick(() => {
                            _forEach(this.items.map(i => i.id), (id) => this.renderSvgIcon(id));
                        });
                    }
                });
                bus.$on('clearAllDropdowns', () => {
                    this.items = this.items.map(i => i ? { ...i, selected: false } : i);
                    if (this.showIcons) {
                        this.$nextTick(() => {
                            _forEach(this.items.map(i => i.id), (id) => this.renderSvgIcon(id));
                        });
                    }
                });
            }
        },
        mounted() {
            if (this.$parent && Array.isArray(this.$parent.initialSelectedFilters)) {
                this.$parent.initialSelectedFilters = _concat(this.$parent.initialSelectedFilters, this.selectedItems.map(i => i.id));
            }
            if (this.showIcons) {
                this.$nextTick(() => {
                    _forEach(this.items.map(i => i.id), (id) => this.renderSvgIcon(id));
                });
            }
        },
        watch: {
            items(newVal) {
                this.selectedItems = newVal.filter(item => item.selected === true);
            },
            isOpen(newVal) {
                if (!newVal) {
                    this.searchQuery = '';
                } else if (this.showSearch) {
                    this.$nextTick(() => {
                        if (this.$refs.searchInput) {
                            this.$refs.searchInput.focusInput();
                        }
                    });
                }
            },
            searchQuery() {
                if (this.showIcons) {
                    this.$nextTick(() => {
                        _forEach(this.unselectedItems.map(i => i.id), (id) => this.renderSvgIcon(id));
                    });
                }
            }
        },
        methods: {
            onSearchInput(value) {
                this.searchQuery = value;
            },
            toggleMenu() {
                if (this.unselectedItems.length > 0 || window.innerWidth <= 721) {
                    this.$emit('opening', this);
                    this.isOpen = !this.isOpen;
                }
            },
            itemSelected(item) {
                this.items = this.items.map(i => i === item ? { ...i, selected: true } : i);
                this.$emit('selection', item.id);
                this.$nextTick(() => {
                    this.renderSvgIcon(item.id);
                    this.$nextTick(() => {
                        const btn = this.$el.querySelector('button');
                        if (btn) btn.focus();
                    });
                });
            },
            itemUnselected(item) {
                this.items = this.items.map(i => i === item ? { ...i, selected: false } : i);
                this.$emit('unselection', item.id);
                this.$nextTick(() => this.renderSvgIcon(item.id));
            },
            renderSvgIcon(id) {
                const container = document.getElementById('multi-inline-' + this.indexId);
                if (!container) return;
                const svgNode = container.querySelector('[id=svg-' + id + ']');
                if (!svgNode) return;
                const svg = svgNode.cloneNode(true);
                const target = this.$el.querySelector('.customIcon[data-svg="' + id + '"]');
                if (target) {
                    target.innerHTML = '';
                    target.appendChild(svg);
                }
            }
        }
    };
</script>